import { AfterViewInit, Component, Input, inject } from "@angular/core";
import { NEVER, of } from "rxjs";
import { finalize, map, switchMap, tap } from "rxjs/operators";
import { AnlageSearchComponent } from "src/app/anlage-api/components/search/anlage-search.component";
import { AnlageApiFaciliyCreateDialogComponent } from "src/app/anlage-api/dialogs/facility-create/facility-create.component";
import {
  AnlageApiFaciliyUpdateDialogComponent,
  IAnlageApiFaciliyUpdateDialogData,
} from "src/app/anlage-api/dialogs/facility-update/facility-update.component";
import {
  CstListViewDataSource,
  ICstGuiCommand,
  ICstListViewColumn,
  ICstListViewSelectionChanged,
  ICstListViewState,
} from "src/app/common-components/list-view/models";
import { CstResponsivePopupService } from "src/app/common-components/resp-popup/services/resp-popup.service";
import { AnlageViewModel } from "src/app/planung-api/view-models";
import { CstNavigationService } from "src/app/services/cst-navigation.service";
import { UserService } from "src/app/services/user.service";
import {
  AnlageDetailDto,
  AnlageLookupDto,
  AnlagenClientProvider,
  ContractsClientProvider,
  FacilitiesAssignToPseudoContractCommand,
  FacilitiesUnassignFromPseudoContractCommand,
} from "src/app/web-api/web-api";
import { InfoportalApiCustomerFacilitiesManagePopupComponent } from "../popup/customer-facilities-manage-popup.component";
import { CstDialogsService } from "src/app/common-components/dialogs/cst-dialogs.service";

@Component({
    selector: "infoportal-api-customer-facilities-manage",
    templateUrl: "./customer-facilities-manage.component.html",
    styleUrls: ["./customer-facilities-manage.component.scss"],
    standalone: false
})
export class InfoportalApiCustomerFacilitiesManageComponent
  implements AfterViewInit
{
  private _contractsClient = inject(ContractsClientProvider);
  private _facilityClient = inject(AnlagenClientProvider);
  private _popupService = inject(CstResponsivePopupService);
  private _dlgService = inject(CstDialogsService);
  private _userService = inject(UserService);
  private _navService = inject(CstNavigationService);

  private _cmdEntryClick: ICstGuiCommand<AnlageLookupDto> = {
    label: $localize`:@@InfoPortal_CustomerFacilitiesManage_FacilityOpen:Facility Dashboard`,
    icon: "dashboard",
    execute: (anlage) => {
      if (this.parent) {
        this.parent.close();
      }
      this._navService.navigateTo_Infoportal_Elevators(anlage.anlageID);
    },
  };
  private _cmdFacilityEdit: ICstGuiCommand<AnlageLookupDto> = {
    label: $localize`:@@InfoPortal_CustomerFacilitiesManage_FacilityEdit:Edit`,
    icon: "edit",
    tooltip: "",
    execute: this._onFacilityUpdate.bind(this),
    canExecute: this._canFacilityUpdate.bind(this),
  };

  @Input() public customerID = 0;
  @Input() public parent: InfoportalApiCustomerFacilitiesManagePopupComponent;

  public selected: AnlageLookupDto[] = [];

  public dataSource = new CstListViewDataSource<AnlageLookupDto>({
    pagination: { pageSize: 10 },
    sorting: {
      enableServerSideSorting: true,
      column: "facilityNoCombined",
      direction: "asc",
    },
  });
  public columns: ICstListViewColumn<AnlageLookupDto>[] = [
    {
      field: "facilityNoCombined",
      header: $localize`:@@InfoPortal_CustomerFacilitiesManage_Header_FacilityNo:Facility No.`,
      command: this._cmdEntryClick,
      width: "25%",
    },
    {
      field: "label",
      header: $localize`:@@InfoPortal_CustomerFacilitiesManage_Header_Label:Label`,
      width: "25%",
    },
    {
      field: "standort",
      header: $localize`:@@InfoPortal_CustomerFacilitiesManage_Header_Location:Location`,
      width: "50%",
    },
  ];
  public actions: ICstGuiCommand<AnlageLookupDto>[] = [
    this._cmdFacilityEdit,
    this._cmdEntryClick,
  ];

  public constructor() {
    this.dataSource.registerFetchDataCallback(this._onFetchData.bind(this));
  }

  ngAfterViewInit(): void {
    if (!this.customerID) {
      return;
    }
    setTimeout(this.dataSource.refresh.bind(this.dataSource), 200);
  }

  public onFacilityCreate() {
    const ref = this._popupService.open<
      AnlageApiFaciliyCreateDialogComponent,
      any,
      AnlageDetailDto
    >(AnlageApiFaciliyCreateDialogComponent);
    ref
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (!result) {
            return NEVER;
          }
          return this._assignFacilityIDs$([result]);
        })
      )
      .subscribe();
  }
  public canFacilityCreate() {
    return this.customerID ? true : false;
  }

  private _onFacilityUpdate(item: AnlageLookupDto) {
    if (!this._canFacilityUpdate(item)) {
      return;
    }
    const ref = this._popupService.open<
      AnlageApiFaciliyUpdateDialogComponent,
      IAnlageApiFaciliyUpdateDialogData,
      AnlageDetailDto
    >(AnlageApiFaciliyUpdateDialogComponent, { data: { id: item.anlageID } });
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.dataSource.refresh();
    });
  }
  private _canFacilityUpdate(item: AnlageLookupDto) {
    return (
      this._userService.CurrentUser.internalAccess &&
      this.customerID &&
      item.anlageID
    );
  }

  public onFacilitiesSelectAndAssign() {
    const popup = this._popupService.open(AnlageSearchComponent);
    popup
      .afterClosed()
      .pipe(
        switchMap((result: AnlageViewModel) => {
          if (!result) {
            return NEVER;
          }
          return this._assignFacilityIDs$([result]);
        })
      )
      .subscribe();
  }
  public canFacilitiesSelectAndAssign() {
    return this.customerID ? true : false;
  }

  public onFacilitiesUnassign() {
    if (this.selected.length == 0) {
      return;
    }
    this.dataSource.loading$.next(true);
    const command = new FacilitiesUnassignFromPseudoContractCommand({
      addressID: this.customerID,
      facilityIDs: this.selected.map((x) => x.anlageID),
    });
    this._contractsClient
      .facilitiesUnassignFromPseudoContract(command)
      .pipe(finalize(() => this.dataSource.loading$.next(false)))
      .subscribe(() => {
        this.selected = [];
        this.dataSource.refresh();
      });
  }

  public onSelectionChanged(
    event: ICstListViewSelectionChanged<AnlageLookupDto>
  ) {
    this.selected = event.selected;
  }

  private _assignFacilityIDs$(
    facilities: { anlageID?: number; anlageNrIntern?: string }[]
  ) {
    this.dataSource.loading$.next(true);
    const command = new FacilitiesAssignToPseudoContractCommand({
      addressID: this.customerID,
      facilityIDs: facilities.map((f) => f.anlageID),
    });
    return this._contractsClient.facilitiesAssignToPseudoContract(command).pipe(
      finalize(() => this.dataSource.loading$.next(false)),
      tap((result) => {
        if (result.success) {
          this.dataSource.refresh();
        }
      }),
      switchMap((res) => {
        if (!res.hasWarnings) {
          return of(res);
        }
        const warnings =
          res.warnings
            .map(
              (x) =>
                facilities.find((f) => f.anlageID == x.facilityID)
                  ?.anlageNrIntern +
                ": " +
                x.message
            )
            .join("\r\n - ") || "";
        return this._dlgService.Information(
          $localize`:@@InfoPortal_CustomerFacilitiesManage_AssignFacilities_WarningDialog:The following facilities could not be assigned to the Customer:\r\n - ${warnings}`
        );
      })
    );
  }

  private _onFetchData(state: ICstListViewState) {
    return this._facilityClient
      .getList(
        undefined,
        undefined,
        undefined,
        undefined,
        [this.customerID],
        undefined,
        undefined,
        false,
        state.pagination?.offset || 0,
        state?.pagination?.pageSize || 10,
        state?.sorting
          ? `${state.sorting.column} ${state.sorting.direction}`
          : undefined
      )
      .pipe(
        map((result) => {
          return { data: result.data, total: result.total };
        })
      );
  }
}
